import { makeStyles, mergeClasses, tokens, Tooltip } from '@fluentui/react-components';
import { DocumentMultipleRegular, HomeRegular, SettingsRegular } from '@fluentui/react-icons';
import type { JSX } from 'react';
import { NavLink } from 'react-router';

import { paths } from '@/routes/paths';

export const NAV_WIDTH_EXPANDED = '208px';
export const NAV_WIDTH_COLLAPSED = '48px';

const useStyles = makeStyles({
  nav: {
    backgroundColor: tokens.colorNeutralBackground1,
    // Longhands: Griffel rejects shorthands that expand to several properties.
    borderInlineEndWidth: tokens.strokeWidthThin,
    borderInlineEndStyle: 'solid',
    borderInlineEndColor: tokens.colorNeutralStroke2,
    paddingBlock: tokens.spacingVerticalS,
    overflowX: 'hidden',
    overflowY: 'auto',
    transitionProperty: 'width',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
  },
  expanded: { width: NAV_WIDTH_EXPANDED },
  collapsed: { width: NAV_WIDTH_COLLAPSED },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    // The accent border is always present but transparent when inactive, so
    // the label never shifts sideways on selection.
    borderInlineStartWidth: tokens.strokeWidthThicker,
    borderInlineStartStyle: 'solid',
    borderInlineStartColor: 'transparent',
    paddingBlock: tokens.spacingVerticalS,
    paddingInlineStart: tokens.spacingHorizontalM,
    paddingInlineEnd: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground1,
    textDecorationLine: 'none',
    fontSize: tokens.fontSizeBase300,
    whiteSpace: 'nowrap',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    ':focus-visible': {
      outlineWidth: tokens.strokeWidthThick,
      outlineStyle: 'solid',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '-2px',
    },
  },
  linkActive: {
    // All three from theme tokens, so light and dark both work without a
    // single hard-coded colour.
    borderInlineStartColor: tokens.colorCompoundBrandForeground1,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorNeutralForeground2BrandSelected,
    fontWeight: tokens.fontWeightSemibold,
  },
  icon: {
    display: 'flex',
    fontSize: '20px',
    flexShrink: 0,
  },
  label: { overflow: 'hidden', textOverflow: 'ellipsis' },
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
  },
});

interface NavEntry {
  to: string;
  label: string;
  icon: JSX.Element;
  end?: boolean;
}

const ENTRIES: NavEntry[] = [
  { to: paths.dashboard, label: 'Dashboard', icon: <HomeRegular /> },
  { to: paths.documents, label: 'Documents', icon: <DocumentMultipleRegular /> },
  { to: paths.settings, label: 'Settings', icon: <SettingsRegular /> },
];

interface SideNavProps {
  collapsed: boolean;
  /** Set when rendered inside the mobile drawer, where collapsing makes no sense. */
  forceExpanded?: boolean;
  onNavigate?: () => void;
}

export function SideNav({ collapsed, forceExpanded = false, onNavigate }: SideNavProps) {
  const styles = useStyles();
  const isCollapsed = collapsed && !forceExpanded;

  return (
    <nav
      aria-label="Main"
      className={mergeClasses(styles.nav, isCollapsed ? styles.collapsed : styles.expanded)}
    >
      <ul className={styles.list}>
        {ENTRIES.map((entry) => (
          <li key={entry.to}>
            <Tooltip
              content={entry.label}
              relationship="label"
              positioning="after"
              // Only useful when the label is hidden; otherwise it just repeats
              // text that is already on screen.
              visible={isCollapsed ? undefined : false}
            >
              <NavLink
                to={entry.to}
                end={entry.end}
                onClick={onNavigate}
                className={({ isActive }) =>
                  mergeClasses(styles.link, isActive && styles.linkActive)
                }
              >
                <span className={styles.icon} aria-hidden="true">
                  {entry.icon}
                </span>
                {/* Kept in the accessibility tree when collapsed, so the link
                    always has an accessible name even without the tooltip. */}
                <span className={isCollapsed ? styles.srOnly : styles.label}>{entry.label}</span>
              </NavLink>
            </Tooltip>
          </li>
        ))}
      </ul>
    </nav>
  );
}
